/**
 * Modo claro / oscuro (ADR 048). Helpers PUROS y testeables, separados del
 * provider de React.
 *
 * El **modo** (claro/oscuro/sistema) es una preferencia del USUARIO, ortogonal
 * al **branding** (color de marca por institución, ADR 039/047). Se persiste por
 * navegador en `localStorage`. La capa visual la implementa `globals.css` vía la
 * clase `.dark` en `<html>`: estos helpers sólo deciden cuándo ponerla.
 */

/** Preferencia explícita del usuario. `sistema` sigue al SO. */
export type PreferenciaTema = "claro" | "oscuro" | "sistema";
/** Tema realmente aplicado (resuelto desde la preferencia + el SO). */
export type TemaEfectivo = "claro" | "oscuro";

export const TEMA_STORAGE_KEY = "copi_tema";
export const MEDIA_OSCURO = "(prefers-color-scheme: dark)";

/** Orden del ciclo del toggle: claro → oscuro → sistema → claro… */
const CICLO: readonly PreferenciaTema[] = ["claro", "oscuro", "sistema"];

export function esPreferenciaTema(valor: unknown): valor is PreferenciaTema {
  return valor === "claro" || valor === "oscuro" || valor === "sistema";
}

/** Lee la preferencia guardada; `sistema` por defecto (falla cerrado). */
export function leerPreferencia(): PreferenciaTema {
  try {
    const v = localStorage.getItem(TEMA_STORAGE_KEY);
    return esPreferenciaTema(v) ? v : "sistema";
  } catch {
    return "sistema";
  }
}

export function guardarPreferencia(pref: PreferenciaTema): void {
  try {
    localStorage.setItem(TEMA_STORAGE_KEY, pref);
  } catch {
    // Modo privado / storage deshabilitado: el tema vivirá sólo en memoria.
  }
}

/** Resuelve el tema efectivo combinando la preferencia con el estado del SO. */
export function resolverTemaEfectivo(
  pref: PreferenciaTema,
  sistemaPrefiereOscuro: boolean,
): TemaEfectivo {
  if (pref === "sistema") return sistemaPrefiereOscuro ? "oscuro" : "claro";
  return pref;
}

/** Aplica/quita la clase `.dark` en `<html>` según el tema efectivo. */
export function aplicarClaseTema(efectivo: TemaEfectivo): void {
  document.documentElement.classList.toggle("dark", efectivo === "oscuro");
}

/** Siguiente preferencia en el ciclo del toggle. */
export function siguientePreferencia(pref: PreferenciaTema): PreferenciaTema {
  const i = CICLO.indexOf(pref);
  return CICLO[(i + 1) % CICLO.length] ?? "sistema";
}

/** ¿El SO prefiere oscuro? Robusto a entornos sin `matchMedia`. */
export function sistemaPrefiereOscuro(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MEDIA_OSCURO).matches;
}
